// api.ts — TanStack Query hooks for the STOCKS module (server state).
// All server data flows through here; components never fetch directly.
//
// Mock mode: hooks hit the /stk/* MSW handlers, which own the state
// transitions (posting, allocation, tray lifecycle, approvals).
// Real-API mode: paths resolve via api/paths.ts; unmapped endpoints
// short-circuit to an empty list so the UI keeps rendering while the
// backend read surfaces (backorders, trays, part-packs) are sliced in.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchList } from '@/api/client';
import { resolvePath, useRealApi } from '@/api/paths';
import { unwrapPage } from '@/api/adapters';
import {
  adjustmentToUi,
  backorderToUi,
  inwardToEntry,
  outwardToDispenseOrder,
  outwardToTray,
  partPackToUi,
  outwardToOtcSale,
  recallToUi,
  stockAlertToStockAlert,
  stockReturnToUi,
  transferToUi,
} from '@/api/stocksAdapters';
import type { PrescriptionDto } from '@/api/rxAdapters';
import { useStore } from '@/store/store';
import { parseDate } from '@/domain/dates';
import type {
  Backorder,
  BackorderStatus,
  DispenseOrder,
  InwardEntry,
  InwardLine,
  OtcSale,
  PartPack,
  RecallUi,
  StockAdjustment,
  StockAlert,
  StockReturnUi,
  StockTransfer,
  StockTransferItem,
  Tray,
  TrayStage,
} from '@/domain/types';
import { fetchPrescriptionsByIds, prescriptionIdsOf } from './rxByIds';

export const stocksKeys = {
  inward: () => ['stocks', 'inward'] as const,
  dispense: () => ['stocks', 'dispense'] as const,
  trays: () => ['stocks', 'trays'] as const,
  trayMaster: () => ['stocks', 'tray-master'] as const,
  partpacks: () => ['stocks', 'partpacks'] as const,
  transfers: () => ['stocks', 'transfers'] as const,
  adjustments: () => ['stocks', 'adjustments'] as const,
  backorders: () => ['stocks', 'backorders'] as const,
  alerts: () => ['stocks', 'alerts'] as const,
  returns: () => ['stocks', 'returns'] as const,
  recalls: () => ['stocks', 'recalls'] as const,
  otc: () => ['stocks', 'otc'] as const,
};

/** Fetch a path that may be unmapped in real-API mode: returns [] when null.
 *  In real-API mode the paginated envelope is unwrapped and each row is mapped
 *  through the module's DTO adapter; mock rows already use the UI shapes. */
function useListQuery<TUi>(mockPath: string, key: readonly unknown[], map?: (raw: unknown) => TUi) {
  const resolved = resolvePath(mockPath);
  return useQuery({
    queryKey: [...key, resolved?.path ?? 'noop'],
    enabled: resolved !== null,
    queryFn: async () => {
      if (!resolved) return [] as TUi[];
      const data = await apiFetchList<unknown>(resolved.path);
      const rows = resolved.paginated ? unwrapPage<unknown>(data) : (data as unknown[]);
      return useRealApi && map ? rows.map(map) : (rows as TUi[]);
    },
  });
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/** Inward entries; real-API mode joins the header and line collections. */
export function useInward() {
  // Scope to the active store (like the Basket Manager) so a multi-store user's Inward list doesn't
  // surface other in-scope stores' GRNs (Bug 24, same class). "ALL" = unscoped.
  const { store } = useStore();
  const storeId = store && store !== 'ALL' ? store : undefined;
  const resolved = resolvePath('/stk/inward');
  const lines = resolvePath('/stk/inward-lines');
  return useQuery({
    queryKey: [...stocksKeys.inward(), storeId ?? 'all', resolved?.path ?? 'noop'],
    enabled: resolved !== null,
    queryFn: async (): Promise<InwardEntry[]> => {
      if (!resolved) return [];
      if (!useRealApi) return apiFetch<InwardEntry[]>(resolved.path);
      const inwardPath = storeId
        ? `${resolved.path}?store_id=${encodeURIComponent(storeId)}`
        : resolved.path;
      const [headers, allLines] = await Promise.all([
        apiFetch<unknown>(inwardPath),
        lines ? apiFetch<unknown>(lines.path) : Promise.resolve([]),
      ]);
      const lineRows = unwrapPage<unknown>(allLines);
      return unwrapPage<unknown>(headers).map((h) => inwardToEntry(h, lineRows));
    },
  });
}

/** Dispense queue; real-API mode joins the linked prescriptions so the rows
 *  carry patient/prescriber/CD and per-line allocation detail. The join is
 *  best-effort: if /api/prescriptions/ is unavailable the bare headers render. */
export function useDispense() {
  // Scope the dispense queue to the active store (like the Basket Manager) so a multi-store user
  // doesn't see another in-scope store's outward rows in the queue (Bug 24, same class).
  const { store } = useStore();
  const storeId = store && store !== 'ALL' ? store : undefined;
  const resolved = resolvePath('/stk/dispense');
  return useQuery({
    queryKey: [...stocksKeys.dispense(), storeId ?? 'all', resolved?.path ?? 'noop'],
    enabled: resolved !== null,
    queryFn: async (): Promise<DispenseOrder[]> => {
      if (!resolved) return [];
      if (!useRealApi) return apiFetch<DispenseOrder[]>(resolved.path);
      // Page through BOTH lists — DRF paginates at 50, so a single fetch silently
      // drops every outward/prescription past page 1, making them unreachable from
      // the queue AND the detail (which resolves the order from this same list).
      const dispensePath = storeId
        ? `${resolved.path}?store_id=${encodeURIComponent(storeId)}`
        : resolved.path;
      // A1: fetch only the prescriptions these outwards reference, not the whole list.
      const headers = await apiFetchList<unknown>(dispensePath);
      const prescriptions = await fetchPrescriptionsByIds<PrescriptionDto>(
        prescriptionIdsOf(headers),
      ).catch(() => []);
      const rxById = new Map(prescriptions.map((rx) => [rx.id, rx]));
      return headers.map((h) => outwardToDispenseOrder(h, rxById));
    },
  });
}

/** Fetch ONE dispense order (by outward uuid OR human reference) + only its prescription — instead
 *  of paging every outward + prescription to `.find()` the one. Powers the fast dispense detail. */
export function useDispenseOrder(idOrRef: string | undefined) {
  const resolved = resolvePath('/stk/dispense');
  return useQuery({
    queryKey: [...stocksKeys.dispense(), 'one', idOrRef ?? ''],
    enabled: !!idOrRef && resolved !== null,
    queryFn: async (): Promise<DispenseOrder | null> => {
      if (!resolved || !idOrRef) return null;
      if (!useRealApi) {
        const all = await apiFetch<DispenseOrder[]>(resolved.path);
        return all.find((d) => d.id === idOrRef || d.uuid === idOrRef) ?? null;
      }
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        idOrRef,
      );
      let header: unknown;
      if (isUuid) {
        header = await apiFetch<unknown>(`${resolved.path}${idOrRef}/`).catch(() => undefined);
      }
      if (!header) {
        const env = await apiFetch<{ results?: unknown[] } | unknown[]>(resolved.path, {
          query: { reference: idOrRef },
        });
        const list = Array.isArray(env) ? env : (env.results ?? []);
        header = list[0];
      }
      if (!header) return null;
      const rxId = (header as { prescription_id?: string }).prescription_id;
      let rxById: Map<string, PrescriptionDto> | undefined;
      if (rxId) {
        const rx = await apiFetch<PrescriptionDto>(`/api/prescriptions/${rxId}/`).catch(
          () => undefined,
        );
        if (rx) rxById = new Map([[rx.id, rx]]);
      }
      return outwardToDispenseOrder(header, rxById);
    },
  });
}
/** Tray view: real-API mode joins prescriptions for patient names. */
export function useTrays() {
  // Scope the Basket Manager to the active store. Baskets/trays are physical per-store items, so
  // without store_id the list returns EVERY in-scope store's baskets — a multi-store (e.g.
  // London + Scotland) user then saw the other store's baskets/Rx here (Bug 24). "ALL" = unscoped.
  const { store } = useStore();
  const storeId = store && store !== 'ALL' ? store : undefined;
  const resolved = resolvePath('/stk/trays');
  return useQuery({
    queryKey: [...stocksKeys.trays(), storeId ?? 'all', resolved?.path ?? 'noop'],
    enabled: resolved !== null,
    queryFn: async (): Promise<Tray[]> => {
      if (!resolved) return [];
      if (!useRealApi) return apiFetch<Tray[]>(resolved.path);
      const trayPath = storeId
        ? `${resolved.path}?store_id=${encodeURIComponent(storeId)}`
        : resolved.path;
      // Page through both lists so trays past page 1 (and their patient names) aren't dropped.
      // A1: fetch only the prescriptions these trays reference, not the whole list.
      const headers = await apiFetchList<unknown>(trayPath);
      const prescriptions = await fetchPrescriptionsByIds<PrescriptionDto>(
        prescriptionIdsOf(headers),
      ).catch(() => []);
      const rxById = new Map(prescriptions.map((rx) => [rx.id, rx]));
      return headers.map((h) => outwardToTray(h, rxById));
    },
  });
}

/** Available physical trays from TrayMaster (status=available, not assigned). */
interface TrayMasterDto {
  id: string;
  tray_number: string;
  tray_status: string;
  tray_label?: string;
  current_outward_id?: string | null;
}
export function useAvailableTrays(storeId?: string) {
  const resolved = resolvePath('/stk/tray-master/available');
  return useQuery({
    queryKey: [...stocksKeys.trayMaster(), 'available', storeId ?? 'any', resolved?.path ?? 'noop'],
    enabled: resolved !== null,
    queryFn: async (): Promise<{ id: string; trayNumber: string }[]> => {
      if (!resolved) return [];
      // Trays are physical per-store baskets: without store_id the endpoint returns
      // every in-scope store's trays and a wrong-store tray can be bound to an order.
      const url = storeId
        ? `${resolved.path}?store_id=${encodeURIComponent(storeId)}`
        : resolved.path;
      const data = await apiFetch<TrayMasterDto[] | { results?: TrayMasterDto[] }>(url);
      const items = Array.isArray(data) ? data : (data.results ?? []);
      return items.map((t) => ({ id: t.id, trayNumber: t.tray_number }));
    },
  });
}

export const usePartPacks = () =>
  useListQuery<PartPack>('/stk/partpacks', stocksKeys.partpacks(), partPackToUi);

// ─── Dispense plan (open-pack-first preview) ───────────────────────────────────

export interface DispenseDraw {
  kind: 'open' | 'sealed';
  batch_id: string;
  batch_number: string;
  part_pack_id: string | null;
  qty: number;
  expiry: string;
  near_expiry: boolean;
  units_per_pack: number;
}
export interface DispensePlan {
  sku: string;
  requested: number;
  fulfilled: number;
  shortfall: number;
  draws: DispenseDraw[];
}

/** Advisory open-pack-first draw plan for `qty` units of `sku` (real-API only). */
export function useDispensePlan(sku: string, qty: number, storeId: string, enabled = true) {
  return useQuery({
    queryKey: ['stocks', 'dispense-plan', sku, qty, storeId],
    enabled: enabled && useRealApi && !!sku && qty > 0 && !!storeId,
    queryFn: () =>
      apiFetch<DispensePlan>('api/stocks/part-packs/dispense-plan/', {
        query: { sku, qty, store_id: storeId },
      }),
  });
}
export const useStockTransfers = () =>
  useListQuery<StockTransfer>('/stk/transfers', stocksKeys.transfers(), transferToUi);
export const useStockAdjustments = () =>
  useListQuery<StockAdjustment>('/stk/adjustments', stocksKeys.adjustments(), adjustmentToUi);
export const useBackorders = () =>
  useListQuery<Backorder>('/stk/backorders', stocksKeys.backorders(), backorderToUi);
export const useStockAlerts = () =>
  useListQuery<StockAlert>('/stk/alerts', stocksKeys.alerts(), stockAlertToStockAlert);
// ─── Mutations ────────────────────────────────────────────────────────────────
// Every mutation carries the acting user so the mock audit trail matches the
// backend's "no state change without its audit event" rule. In real-API mode
// each action resolves to its DRF route (ledger-writing actions send an
// Idempotency-Key header); actions the backend does not expose yet reject with
// a clear message surfaced by the global mutation error toast.

interface RealAction {
  path: string;
  method?: 'POST' | 'PATCH';
  body?: object;
  /** ledger-writing actions send an Idempotency-Key header */
  idempotent?: boolean;
}

const NOT_WIRED = 'This action is not available on the live API yet (mock mode only)';

function runReal(spec: RealAction | null): Promise<unknown> {
  if (!spec) return Promise.reject(new Error(NOT_WIRED));
  return apiFetch<unknown>(spec.path, {
    method: spec.method ?? 'POST',
    body: spec.body ?? {},
    headers: spec.idempotent ? { 'Idempotency-Key': crypto.randomUUID() } : undefined,
  });
}

/** POST to a /stk action path (mock) or the mapped DRF action (real). */
function useStockAction<TBody extends object>(
  mockPath: (body: TBody & { id: string }) => string,
  keys: ReadonlyArray<readonly unknown[]>,
  real?: (body: TBody & { id: string }) => RealAction | null,
) {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (body: TBody & { id: string }) => {
      if (useRealApi) return runReal(real?.(body) ?? null);
      return apiFetch<unknown>(mockPath(body), {
        method: 'POST',
        body: { ...body, who: me, role },
      });
    },
    onSuccess: () => keys.forEach((key) => qc.invalidateQueries({ queryKey: key })),
  });
}

/** dd/mm/yyyy (UI) → yyyy-mm-dd (DRF DateField). */
function isoDate(uk: string): string {
  const d = parseDate(uk);
  return Number.isNaN(d.getTime()) ? uk : d.toISOString().slice(0, 10);
}

const SCAN_TYPE_CODE: Record<InwardLine['scan'], string> = {
  Scan: 'barcode',
  Manual: 'manual',
  OCR: 'ocr',
  CSV: 'manual',
  Transfer: 'manual',
};

// Inward
export interface CreateInwardInput {
  date: string;
  supplier: string;
  po: string;
  store: string;
  lines: InwardLine[];
  docs: string[];
}

export function useCreateInward() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (entry: CreateInwardInput) => {
      if (!useRealApi) {
        return apiFetch<InwardEntry>('/stk/inward', {
          method: 'POST',
          body: { ...entry, who: me, role },
        });
      }
      const header = await apiFetch<{ id: string }>('/api/stocks/inward/', {
        method: 'POST',
        body: {
          store_id: entry.store,
          reference: entry.po === '—' ? `GRN-${Date.now()}` : entry.po,
          supplier: entry.supplier,
        },
      });
      for (const line of entry.lines) {
        await apiFetch('/api/stocks/inward-lines/', {
          method: 'POST',
          body: {
            inward: header.id,
            sku: line.sku,
            batch_number: line.batch,
            expiry_date: isoDate(line.exp),
            qty_received: line.qty,
            scan_type: SCAN_TYPE_CODE[line.scan] ?? 'manual',
          },
        });
      }
      return header;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: stocksKeys.inward() }),
  });
}

const inwardKeys = [stocksKeys.inward()] as const;
// Posting goods-in also fulfils open backorders server-side — refresh the register.
const inwardPostKeys = [stocksKeys.inward(), ['inventory'], stocksKeys.backorders()] as const;

export const useSubmitInward = () =>
  useStockAction<object>(
    ({ id }) => `/stk/inward/${id}/submit`,
    inwardKeys,
    ({ id }) => ({
      path: `/api/stocks/inward/${id}/`,
      method: 'PATCH',
      body: { status: 'ready_to_post' },
    }),
  );
export const usePostInward = () =>
  useStockAction<{ allowExpired?: boolean }>(
    ({ id }) => `/stk/inward/${id}/post`,
    inwardPostKeys,
    ({ id, allowExpired }) => ({
      path: `/api/stocks/inward/${id}/post_entry/`,
      body: { allow_expired: !!allowExpired },
      idempotent: true,
    }),
  );
export const useVoidInward = () =>
  useStockAction<object>(
    ({ id }) => `/stk/inward/${id}/void`,
    inwardKeys,
    ({ id }) => ({
      path: `/api/stocks/inward/${id}/`,
      method: 'PATCH',
      body: { status: 'voided' },
    }),
  );
// Expired-line approval is a mock-only flow until the backend exposes a
// line-decision route (the OCR verify path covers a different scenario).
export const useDecideInwardLine = () =>
  useStockAction<{ lineIdx: number; decision: 'Approved' | 'Rejected' }>(
    ({ id }) => `/stk/inward/${id}/line-decision`,
    inwardKeys,
  );

// Dispense
const stageLogKey = ['stocks', 'stage-logs'] as const;
const dispenseKeys = [stocksKeys.dispense(), stocksKeys.trays(), stageLogKey] as const;
const dispensePostKeys = [
  stocksKeys.dispense(),
  stocksKeys.trays(),
  stocksKeys.trayMaster(),
  stocksKeys.backorders(),
  stageLogKey,
  ['inventory'],
] as const;

export const useAssignTray = () =>
  useStockAction<{
    tray?: string;
    station?: string;
    store?: string;
    rxUuid?: string;
    createNew?: boolean;
  }>(
    ({ id }) => `/stk/dispense/${id}/assign-tray`,
    [...dispenseKeys, stocksKeys.trayMaster()],
    ({ id, tray, store, rxUuid, createNew }) => ({
      path: `/api/stocks/tray-master/assign/`,
      body: {
        store_id: store,
        prescription_id: rxUuid || id,
        // "Create new basket" registers + assigns a fresh tray so it works with an empty pool;
        // otherwise assign a chosen/available existing tray.
        tray_id: createNew ? undefined : tray || undefined,
        create_new: createNew || undefined,
      },
    }),
  );
/** Bind a prescription to a basket by its barcode.
 *
 * Prefer this over `useAssignTray`: the backend picks the right path automatically — immediate
 * assignment when the script is already allocated (Standard), or DEFERRED pre-binding when it was
 * printed and dropped in a basket before allocation (print-and-basket). `useAssignTray` only does
 * the immediate path and 409s pre-allocation, which dead-ends the print-and-basket flow.
 */
export const useScanBindTray = () =>
  useStockAction<{ barcode: string; store?: string; rxUuid?: string }>(
    ({ id }) => `/stk/dispense/${id}/scan-bind-tray`,
    [...dispenseKeys, stocksKeys.trayMaster()],
    ({ id, barcode, store, rxUuid }) => ({
      path: `/api/stocks/tray-master/scan-bind/`,
      body: { store_id: store, prescription_id: rxUuid || id, barcode },
    }),
  );

// Line scanning — two-step: verify (read-only check) then confirm (persist result).
export const useScanDispenseLine = () =>
  useStockAction<{ lineIdx: number; barcode?: string }>(
    ({ id }) => `/stk/dispense/${id}/scan`,
    dispenseKeys,
  );

/** Verify a barcode against the allocation (read-only, no state change). */
export function useVerifyScan() {
  return useMutation({
    mutationFn: async (body: { prescriptionId: string; lineId: string; barcode: string }) => {
      return apiFetch<{ result: 'match' | 'mismatch'; detail: Record<string, unknown> }>(
        `/api/prescriptions/${body.prescriptionId}/verify-scan/`,
        { method: 'POST', body: { line_id: body.lineId, barcode: body.barcode } },
      );
    },
  });
}

/** Confirm/record a scan result on the prescription line. Carries the verified-picker token so the
 *  pick attributes to whoever is verified as picking (else the logged-in user). */
export function useConfirmScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      prescriptionId: string;
      lineId: string;
      barcode: string;
      pickerToken?: string;
    }) => {
      return apiFetch<unknown>(`/api/prescriptions/${body.prescriptionId}/confirm-scan/`, {
        method: 'POST',
        body: { line_id: body.lineId, barcode: body.barcode, picker_token: body.pickerToken ?? '' },
      });
    },
    onSuccess: () => {
      dispenseKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
    },
  });
}

/**
 * Record a line as verified WITHOUT a physical barcode scan (explicit result).
 *
 * The scan is the production safety control; this is a test-only affordance that
 * lets an automated (Playwright) run drive the pick → verify → post path end-to-end
 * where a real scanner isn't available. It is only ever wired into the UI behind
 * `import.meta.env.DEV` + an explicit `?e2e=1` opt-in (see DispenseDetail), so it is
 * stripped from production bundles. The backend `confirm-scan` endpoint already
 * accepts `{ line_id, result }` with no barcode.
 */
export function useMarkVerified() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { prescriptionId: string; lineId: string }) => {
      return apiFetch<unknown>(`/api/prescriptions/${body.prescriptionId}/confirm-scan/`, {
        method: 'POST',
        body: { line_id: body.lineId, result: 'match' },
      });
    },
    onSuccess: () => {
      dispenseKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
    },
  });
}
export const useAllocateDispense = () =>
  useStockAction<{ rxUuid?: string }>(
    ({ id }) => `/stk/dispense/${id}/allocate`,
    dispensePostKeys,
    ({ rxUuid }) => (rxUuid ? { path: `/api/prescriptions/${rxUuid}/allocate/` } : null),
  );
export const useReleaseDispense = () =>
  useStockAction<object>(({ id }) => `/stk/dispense/${id}/release`, dispensePostKeys);
export const useOverrideFefo = () =>
  useStockAction<{
    lineIdx: number;
    batch: string;
    exp: string;
    justification: string;
    lineId?: string;
    rxUuid?: string;
  }>(
    ({ id }) => `/stk/dispense/${id}/override`,
    dispenseKeys,
    ({ rxUuid, lineId, batch, justification }) =>
      rxUuid && lineId
        ? {
            path: `/api/prescriptions/${rxUuid}/override_line/`,
            method: 'POST',
            body: { line_id: lineId, batch_stock_id: batch, reason: justification },
          }
        : null,
  );
export const useGenerateTicket = () =>
  useStockAction<object>(
    ({ id }) => `/stk/dispense/${id}/ticket`,
    dispenseKeys,
    ({ id }) => ({
      path: `/api/stocks/outward/${id}/generate-ticket/`,
      method: 'POST',
      body: {},
    }),
  );

export const useAssignBench = () =>
  useStockAction<{ location_id?: string }>(
    ({ id }) => `/stk/dispense/${id}/assign-bench`,
    dispensePostKeys,
    ({ id, location_id }) => ({
      path: `/api/stocks/outward/${id}/assign-bench/`,
      method: 'POST',
      body: location_id ? { location_id } : {},
    }),
  );

/** Move an already-benched job to a different collection location (no status change). */
export const useReassignBench = () =>
  useStockAction<{ location_id?: string }>(
    ({ id }) => `/stk/dispense/${id}/reassign-bench`,
    dispensePostKeys,
    ({ id, location_id }) => ({
      path: `/api/stocks/outward/${id}/reassign-bench/`,
      method: 'POST',
      body: location_id ? { location_id } : {},
    }),
  );

/** Bag & label an outward WITHOUT benching it — the distinct bagging step (mints the bag QR). */
export function useBagOutward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (outwardId: string) =>
      apiFetch<unknown>(`/api/stocks/outward/${outwardId}/bag/`, { method: 'POST', body: {} }),
    onSuccess: () => {
      dispensePostKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      qc.invalidateQueries({ queryKey: ['rx'] });
    },
  });
}

/** Pre-bench "Handle shortage": raise the owing/incomplete backorder(s) for the
 *  job's unavailable lines while the tray is still bound (real-API only). Returns
 *  the open backorders so the caller can drive the decision engine. */
export function useRaiseBackorders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Array<{ id: string }>> => {
      if (!useRealApi) throw new Error(NOT_WIRED);
      return apiFetch<Array<{ id: string }>>(`/api/stocks/outward/${id}/raise-backorders/`, {
        method: 'POST',
        body: {},
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      });
    },
    onSuccess: () => {
      dispensePostKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      qc.invalidateQueries({ queryKey: ['stocks', 'backorders'] });
      qc.invalidateQueries({ queryKey: ['rx'] });
    },
  });
}

export const useAssignStaff = () =>
  useStockAction<{ pharmacist_id?: string; picker_id?: string }>(
    ({ id }) => `/stk/dispense/${id}/assign-staff`,
    dispensePostKeys,
    ({ id, pharmacist_id, picker_id }) => ({
      path: `/api/stocks/outward/${id}/assign-staff/`,
      method: 'POST',
      body: { pharmacist_id, picker_id },
    }),
  );

export interface StoreUserDto {
  id: string;
  username: string;
  full_name: string;
  email: string;
  is_active: boolean;
  role: string | null;
  store_scope: string[];
  first_name?: string;
  last_name?: string;
}

export function useStoreUsers() {
  return useQuery({
    queryKey: ['identity', 'users'],
    enabled: useRealApi,
    queryFn: () => apiFetch<{ results: StoreUserDto[] } | StoreUserDto[]>('/api/identity/users/'),
    select: (data) => (Array.isArray(data) ? data : (data.results ?? [])),
  });
}

export interface DispenseLocationDto {
  id: string;
  code: string;
  label: string;
  location_type: string;
  capacity: number;
  occupancy: number;
  alerts: { code: string; message: string }[];
}

export function useDispenseLocations(storeId?: string) {
  return useQuery<DispenseLocationDto[]>({
    queryKey: ['stocks', 'dispense-locations', storeId],
    queryFn: async () => {
      if (!useRealApi || !storeId) return [];
      const data = await apiFetch<{ locations: DispenseLocationDto[] }>(
        '/api/stocks/dispense-locations/?store_id=' + storeId,
      );
      return data.locations ?? [];
    },
    enabled: !!storeId,
    staleTime: 30_000,
  });
}

// Outward void (SCN-STK-O12): the /void action releases reservations (Allocated ->
// Free Hold), frees the bound tray, and resets the linked Rx. Posted entries are
// reversal-only (409). Replaces the old bare PATCH, which leaked reservations.
export const useVoidDispense = () =>
  useStockAction<object>(
    ({ id }) => `/stk/dispense/${id}/void`,
    dispensePostKeys,
    ({ id }) => ({ path: `/api/stocks/outward/${id}/void/` }),
  );
export const usePostDispense = () =>
  useStockAction<{
    give: number[];
    boStatus?: BackorderStatus;
    expectedDate?: string;
    witness?: string;
    partialSupplyApproved?: boolean;
    /** batch_id → reason: "open a fresh pack instead of finishing the open one". */
    packSourceOverrides?: Record<string, string>;
  }>(
    ({ id }) => `/stk/dispense/${id}/post`,
    dispensePostKeys,
    ({ id, partialSupplyApproved, packSourceOverrides }) => ({
      // Partial quantities need allocation ids (Prescriptions wiring); full post for now.
      path: `/api/stocks/outward/${id}/post_entry/`,
      body: {
        partial_supply_approved: !!partialSupplyApproved,
        pack_source_overrides: packSourceOverrides ?? {},
      },
      idempotent: true,
    }),
  );

/** Persist (or clear, with an empty reason) a pharmacist pack-source override for a batch —
 *  a picking-stage decision, honoured at post. */
export const useSetPackOverride = () =>
  useStockAction<{ batchId: string; reason: string }>(
    ({ id }) => `/stk/dispense/${id}/pack-override`,
    dispensePostKeys,
    ({ id, batchId, reason }) => ({
      path: `/api/stocks/outward/${id}/pack-override/`,
      body: { batch_id: batchId, reason },
    }),
  );

// Trays
const trayKeys = [
  stocksKeys.trays(),
  stocksKeys.dispense(),
  stocksKeys.trayMaster(),
  stageLogKey,
] as const;

/** UI scan stages → backend scan_tray actions. */
const TRAY_SCAN_ACTION: Record<TrayStage, string> = {
  Assign: 'assign',
  Assembling: 'assign',
  Verification: 'verify',
  Collection: 'collect',
};

export function useCreateTray() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (body: { trayId?: string; rx?: string; station?: string }) => {
      // Real trays are outward records created by the dispense flow.
      if (useRealApi) return Promise.reject(new Error(NOT_WIRED));
      return apiFetch<Tray>('/stk/trays', { method: 'POST', body: { ...body, who: me, role } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: stocksKeys.trays() }),
  });
}

export const useScanTrayStage = () =>
  useStockAction<{ stage: TrayStage; station?: string }>(
    ({ id }) => `/stk/trays/${id}/scan`,
    trayKeys,
    ({ id, stage, station }) => ({
      path: `/api/stocks/outward/${id}/scan_tray/`,
      body: { scan_action: TRAY_SCAN_ACTION[stage], station: station ?? '—' },
    }),
  );
// Cancel and override-complete are folded into outward posting on the backend.
export const useCancelTray = () =>
  useStockAction<{ reason: string }>(({ id }) => `/stk/trays/${id}/cancel`, trayKeys);
export const useCompleteTray = () =>
  useStockAction<{ override?: boolean; reason?: string }>(
    ({ id }) => `/stk/trays/${id}/complete`,
    trayKeys,
  );

// Part-packs
export function useOpenPartPack() {
  const qc = useQueryClient();
  const { me, role, store } = useStore();
  return useMutation({
    mutationFn: (body: Pick<PartPack, 'sku' | 'product' | 'batch' | 'original'>) => {
      if (useRealApi) {
        if (store === 'ALL') return Promise.reject(new Error('Select a store to open a pack'));
        return apiFetch<PartPack>('/api/stocks/part-packs/', {
          method: 'POST',
          body: {
            store_id: store,
            sku: body.sku,
            batch_id: body.batch,
            original_qty: body.original,
          },
        });
      }
      return apiFetch<PartPack>('/stk/partpacks', {
        method: 'POST',
        body: { ...body, who: me, role },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: stocksKeys.partpacks() }),
  });
}

export const useUsePartPack = () =>
  useStockAction<{ qty: number; rx?: string }>(
    ({ id }) => `/stk/partpacks/${id}/use`,
    [stocksKeys.partpacks()],
    ({ id, qty, rx }) => ({
      path: `/api/stocks/part-packs/${id}/use/`,
      body: { qty, reference_type: rx ? 'prescription' : '' },
    }),
  );

// Transfers
export function useCreateStockTransfer() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (body: {
      source: string;
      dest: string;
      date: string;
      items: StockTransferItem[];
    }) => {
      if (useRealApi) {
        // Multi-line: one Transfer row per line, all sharing a single reference (the model is
        // single-SKU per row). `item.batch` carries the batch NUMBER (not the batch UUID).
        const reference = `TRF-${Date.now()}`;
        const results = await Promise.all(
          body.items.map((item) =>
            apiFetch<StockTransfer>('/api/transfers/', {
              method: 'POST',
              body: {
                store_id: body.source,
                source_store_id: body.source,
                dest_store_id: body.dest,
                reference,
                sku: item.sku,
                batch_number: item.batch,
                requested_qty: item.qty,
              },
            }),
          ),
        );
        return results[0];
      }
      return apiFetch<StockTransfer>('/stk/transfers', {
        method: 'POST',
        body: { ...body, who: me, role },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: stocksKeys.transfers() }),
  });
}

const transferKeys = [stocksKeys.transfers(), stocksKeys.inward(), ['inventory']] as const;

export interface TransferLineDto {
  id: string; // transfer line (backend) id
  reference: string;
  sku: string;
  product_name?: string;
  batch_number: string;
  requested_qty: number;
  source_store_id: string;
  dest_store_id: string;
  source_store_name?: string;
  dest_store_name?: string;
  expiry_date?: string | null;
  status: string;
}
interface ResolveTransferResponse {
  reference: string;
  direction: 'inbound' | 'outbound';
  lines: TransferLineDto[];
}

/** Resolve a scanned transfer reference → its lines, scoped to the caller's store. */
export function useResolveTransfer(
  reference: string,
  direction: 'inbound' | 'outbound',
  enabled = true,
) {
  return useQuery({
    queryKey: ['stocks', 'transfer-resolve', direction, reference],
    queryFn: () =>
      apiFetch<ResolveTransferResponse>('/api/stocks/outward/resolve_transfer/', {
        query: { reference, direction },
      }),
    // works in mock mode too (group endpoints are mocked)
    enabled: enabled && !!reference,
  });
}

/** F1: approve (or reject) every waiting line of a transfer box in one decision. The server
 *  applies the same four-eyes rule per line (the requester cannot approve their own box). */
export function useApproveTransferGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reference, decision }: { reference: string; decision: 'approve' | 'reject' }) =>
      apiFetch<{ decided: number; status: string }>('/api/transfers/approve_group/', {
        method: 'POST',
        body: { reference, decision },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: () =>
      [stocksKeys.transfers(), ['multistore'] as const].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

export function useDispatchTransferGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reference: string) =>
      apiFetch<{ dispatched: number }>('/api/stocks/outward/dispatch_transfer_group/', {
        method: 'POST',
        body: { reference },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: () =>
      [stocksKeys.transfers(), ['inventory'] as const].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

export interface ReceiveLineInput {
  transfer_id: string;
  qty_received?: number;
  discrepancy_reason?: string;
}
export function useReceiveTransferGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reference, lines }: { reference: string; lines: ReceiveLineInput[] }) =>
      apiFetch<{ received: number }>('/api/stocks/outward/receive_transfer_group/', {
        method: 'POST',
        body: { reference, lines },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: () =>
      // backorders too: received transfer stock fulfils the destination's owings server-side
      [
        stocksKeys.transfers(),
        stocksKeys.inward(),
        ['inventory'] as const,
        stocksKeys.backorders(),
      ].forEach((key) => qc.invalidateQueries({ queryKey: key })),
  });
}

// Adjustments

/** UI reason labels → backend reason codes (Breakage folds into damage). */
const ADJ_REASON_CODE: Record<string, string> = {
  Expiry: 'expiry',
  Damage: 'damage',
  Recall: 'recall',
  Theft: 'theft',
  Breakage: 'damage',
  'Supplier return': 'return_to_supplier',
};

export function useCreateStockAdjustment() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (
      body: Pick<
        StockAdjustment,
        'sku' | 'product' | 'batch' | 'store' | 'delta' | 'reason' | 'comment' | 'cd'
      > & { batchUuid?: string },
    ) => {
      if (useRealApi) {
        return apiFetch<StockAdjustment>('/api/adjustments/', {
          method: 'POST',
          body: {
            store_id: body.store,
            batch_stock_id: body.batchUuid ?? body.batch,
            qty_delta: body.delta,
            reason_code: ADJ_REASON_CODE[body.reason] ?? 'discrepancy',
            evidence_ref: body.comment,
          },
        });
      }
      return apiFetch<StockAdjustment>('/stk/adjustments', {
        method: 'POST',
        body: { ...body, who: me, role },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: stocksKeys.adjustments() }),
  });
}

export function useDecideStockAdjustment() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (body: { id: string; decision: 'Approved' | 'Rejected' }) => {
      if (!useRealApi) {
        return apiFetch<unknown>(`/stk/adjustments/${body.id}/decision`, {
          method: 'POST',
          body: { ...body, who: me, role },
        });
      }
      const decided = await apiFetch<unknown>(`/api/adjustments/${body.id}/approve/`, {
        method: 'POST',
        body: { decision: body.decision === 'Approved' ? 'approve' : 'reject' },
      });
      if (body.decision !== 'Approved') return decided;
      // approval applies the stock delta — a separate, idempotent ledger write
      return apiFetch<unknown>(`/api/adjustments/${body.id}/apply/`, {
        method: 'POST',
        body: {},
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      });
    },
    onSuccess: () =>
      // backorders too: applying a positive adjustment fulfils open owings server-side
      [stocksKeys.adjustments(), ['inventory'] as const, stocksKeys.backorders()].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

export interface FulfilBatchSelection {
  batch_id: string;
  qty: number;
}
/** Fulfil an owing — optionally from operator-chosen batches (else auto-FEFO). */
export const useFulfilBackorder = () =>
  useStockAction<{ batchSelections?: FulfilBatchSelection[] }>(
    ({ id }) => `/stk/backorders/${id}/fulfil`,
    [stocksKeys.backorders(), ['inventory']],
    ({ id, batchSelections }) => ({
      path: `/api/stocks/backorders/${id}/fulfil/`,
      idempotent: true,
      body: batchSelections?.length ? { batch_selections: batchSelections } : {},
    }),
  );

export interface AvailableBatch {
  id: string;
  batch_number: string;
  expiry_date: string | null;
  free: number;
  bin_location: string;
}
/** Free batches that can fulfil an owing (prefix-insensitive). For the batch-picker. */
export function useBackorderBatches(backorderId: string | undefined) {
  return useQuery({
    queryKey: ['stocks', 'backorders', backorderId ?? 'none', 'batches'],
    enabled: useRealApi && !!backorderId,
    queryFn: async (): Promise<AvailableBatch[]> => {
      if (!useRealApi || !backorderId) return [];
      return apiFetch<AvailableBatch[]>(`/api/stocks/backorders/${backorderId}/available-batches/`);
    },
  });
}

export const useAckStockAlert = () =>
  useStockAction<{ storeId?: string }>(
    ({ id }) => `/stk/alerts/${id}/ack`,
    [stocksKeys.alerts(), ['inventory']],
    ({ id, storeId }) => ({
      path: '/api/inventory/alerts/state/',
      body: { alert_key: id, store_id: storeId, status: 'acknowledged' },
    }),
  );
export const useResolveStockAlert = () =>
  useStockAction<{ label?: string; storeId?: string }>(
    ({ id }) => `/stk/alerts/${id}/resolve`,
    [stocksKeys.alerts(), ['inventory']],
    ({ id, storeId }) => ({
      path: '/api/inventory/alerts/state/',
      body: { alert_key: id, store_id: storeId, status: 'resolved' },
    }),
  );

// Returns & recalls (R01/R02/R03/R05)

export const useReturns = () =>
  useListQuery<StockReturnUi>('/stk/returns', stocksKeys.returns(), stockReturnToUi);
export const useRecalls = () =>
  useListQuery<RecallUi>('/stk/recalls', stocksKeys.recalls(), recallToUi);

export interface PatientReturnInput {
  sku: string;
  product: string;
  batch: string;
  expiry: string;
  qty: number;
  reason: string;
  condition: string;
  reference: string;
  store: string;
}

export function useCreatePatientReturn() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (body: PatientReturnInput) => {
      if (useRealApi) {
        return apiFetch<unknown>('/api/stocks/returns/', {
          method: 'POST',
          body: {
            type: 'patient',
            store_id: body.store,
            sku: body.sku,
            batch_number: body.batch,
            expiry_date: isoDate(body.expiry),
            qty: body.qty,
            reason: body.reason,
            condition: body.condition,
            reference: body.reference,
          },
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        });
      }
      return apiFetch<unknown>('/stk/returns', {
        method: 'POST',
        body: { ...body, type: 'Patient', who: me, role },
      });
    },
    onSuccess: () =>
      [stocksKeys.returns(), ['inventory'] as const].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

export interface SupplierReturnInput {
  sku: string;
  product: string;
  batch: string;
  /** backend batch_stock PK in real-API mode */
  batchUuid?: string;
  qty: number;
  reason: string;
  reference: string;
  store: string;
}

export function useCreateSupplierReturn() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: (body: SupplierReturnInput) => {
      if (useRealApi) {
        return apiFetch<unknown>('/api/stocks/returns/', {
          method: 'POST',
          body: {
            type: 'supplier',
            store_id: body.store,
            batch_stock_id: body.batchUuid ?? body.batch,
            qty: body.qty,
            reason: body.reason,
            reference: body.reference,
          },
        });
      }
      return apiFetch<unknown>('/stk/returns', {
        method: 'POST',
        body: { ...body, type: 'Supplier', who: me, role },
      });
    },
    onSuccess: () =>
      [stocksKeys.returns(), stocksKeys.adjustments()].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

export function useInitiateRecall() {
  const qc = useQueryClient();
  const { me, role, store } = useStore();
  return useMutation({
    mutationFn: (body: { batch: string; sku?: string; reason: string }) => {
      if (useRealApi) {
        if (store === 'ALL')
          return Promise.reject(new Error('Select a store to initiate a recall'));
        return apiFetch<unknown>('/api/inventory/recalls/', {
          method: 'POST',
          body: {
            store_id: store,
            batch_number: body.batch,
            sku: body.sku ?? '',
            reason: body.reason,
          },
        });
      }
      return apiFetch<unknown>('/stk/recalls', {
        method: 'POST',
        body: { ...body, who: me, role },
      });
    },
    onSuccess: () =>
      [stocksKeys.recalls(), ['inventory'] as const].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

// OTC sale (O10 — GSL/P/POM gating; backend enforces the gate on outward post)

export function useOtcSales() {
  return useQuery({
    queryKey: [...stocksKeys.otc(), useRealApi ? 'real' : 'mock'],
    queryFn: async (): Promise<OtcSale[]> => {
      if (!useRealApi) return apiFetch<OtcSale[]>('/stk/otc');
      const data = await apiFetch<unknown>('/api/stocks/outward/', {
        query: { outward_type: 'otc' },
      });
      return unwrapPage<unknown>(data).map(outwardToOtcSale);
    },
  });
}

export function useOtcSale() {
  const qc = useQueryClient();
  const { me, role } = useStore();
  return useMutation({
    mutationFn: async (body: {
      product: string;
      sku: string;
      qty: number;
      store: string;
      pharmacist?: string;
    }) => {
      if (useRealApi) {
        if (body.store === 'ALL') return Promise.reject(new Error('Select a store for OTC sales'));
        const outward = await apiFetch<{ id: string }>('/api/stocks/outward/', {
          method: 'POST',
          body: { store_id: body.store, reference: `OTC-${Date.now()}`, outward_type: 'otc' },
        });
        return apiFetch<unknown>(`/api/stocks/outward/${outward.id}/post_entry/`, {
          method: 'POST',
          body: { otc_pharmacist_approved: !!body.pharmacist },
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        });
      }
      return apiFetch<unknown>('/stk/otc', { method: 'POST', body: { ...body, who: me, role } });
    },
    onSuccess: () =>
      [stocksKeys.otc(), ['inventory'] as const].forEach((key) =>
        qc.invalidateQueries({ queryKey: key }),
      ),
  });
}

// ─── Dispense Assignment, Stage Logs & Notifications ─────────────────────────

export function useDispenseNotifications() {
  return useQuery({
    queryKey: ['stocks', 'notifications'],
    enabled: useRealApi,
    queryFn: () => apiFetch<{ results: DispenseNotificationDto[] }>('/api/stocks/notifications/'),
    select: (data) => data.results ?? [],
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['stocks', 'notifications', 'count'],
    enabled: useRealApi,
    queryFn: () => apiFetch<{ count: number }>('/api/stocks/notifications/count/'),
    select: (data) => data.count,
    refetchInterval: 30_000, // poll every 30s
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<unknown>(`/api/stocks/notifications/${id}/mark-read/`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stocks', 'notifications'] });
    },
  });
}

export function useDispenseAssignmentConfig(storeId: string | undefined) {
  return useQuery({
    queryKey: ['stocks', 'assignment-config', storeId],
    enabled: useRealApi && !!storeId,
    queryFn: () =>
      apiFetch<DispenseAssignmentConfigDto>(`/api/stocks/assignment-config/for-store/${storeId}/`),
  });
}

export function useStageLogs(outwardId: string | undefined) {
  return useQuery({
    queryKey: ['stocks', 'stage-logs', outwardId],
    enabled: useRealApi && !!outwardId,
    queryFn: () =>
      apiFetch<{ results: StageLogDto[] }>(`/api/stocks/stage-logs/?outward=${outwardId}`),
    select: (data) => data.results ?? [],
  });
}

// ─── DTO types for new endpoints ─────────────────────────────────────────────

interface DispenseNotificationDto {
  id: string;
  notification_type: string;
  priority: string;
  title: string;
  message: string;
  is_read: boolean;
  stage: string;
  created_at: string;
}

interface DispenseAssignmentConfigDto {
  id: string;
  default_picker: string | null;
  default_checker: string | null;
  default_dispenser: string | null;
  auto_assign_on_allocation: boolean;
  escalation_threshold_minutes: number;
}

interface StageLogDto {
  id: string;
  stage: string;
  action: string;
  performed_by: string | null;
  performed_by_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  performed_at: string;
  notes: string;
}

// ── Supply Exchange (shared stock-request board) ──────────────────────────────

export interface StockRequestDto {
  id: string;
  reference: string;
  requesting_store_id: string;
  requesting_store_name: string;
  target_store_id: string | null;
  target_store_name: string;
  sku: string;
  product_name: string;
  qty: number;
  priority: string;
  note: string;
  status: 'open' | 'fulfilled' | 'cancelled';
  fulfilled_by_store_id: string | null;
  fulfilled_by_store_name: string;
  fulfilled_transfer_id: string | null;
  fulfilled_at: string | null;
  created_at: string;
  is_open: boolean;
}
interface StockRequestPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: StockRequestDto[];
}

/** Paginated Supply Exchange board. view = 'open' (network board) | 'mine' | 'targeted'. */
/** Supply Exchange board, always scoped to the ACTIVE store (`storeId`) so the sender/receiver
 *  separation is clean: mine = my store's requests, open = other stores' requests I can fulfil.
 *  Served in BOTH tiers: mock mode hits the MSW exchangeHandlers on the same route. */
export function useStockRequests(view: string, page: number, storeId: string) {
  return useQuery({
    queryKey: ['stock-requests', view, page, storeId],
    queryFn: () =>
      apiFetch<StockRequestPage>('/api/transfers/requests/', {
        query: { view, page, store_id: storeId },
      }),
    enabled: !!storeId,
  });
}

export function useCreateStockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      requesting_store_id: string;
      sku: string;
      qty: number;
      target_store_id?: string | null;
      note?: string;
      priority?: string;
    }) => apiFetch<StockRequestDto>('/api/transfers/requests/', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-requests'] }),
  });
}

export function useClaimStockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fulfilling_store_id }: { id: string; fulfilling_store_id: string }) =>
      apiFetch<StockRequestDto & { transfer_id: string }>(`/api/transfers/requests/${id}/claim/`, {
        method: 'POST',
        body: { fulfilling_store_id },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-requests'] });
      qc.invalidateQueries({ queryKey: stocksKeys.transfers() });
      // the claim raises an AWAITING_APPROVAL transfer — refresh the multi-store board too
      qc.invalidateQueries({ queryKey: ['mst'] });
    },
  });
}

export function useCancelStockRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<StockRequestDto>(`/api/transfers/requests/${id}/cancel/`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-requests'] }),
  });
}

// ── Recall task list + return disposition (plan E2) — real-API only ─────────────────────────────

export interface RecallTaskUi {
  id: string;
  storeId: string;
  recallId: string;
  recallReason: string;
  batch: string;
  sku: string;
  expiry: string | null;
  qty: number;
  status: 'open' | 'isolated' | 'disposing' | 'disposed';
  disposition: '' | 'supplier_return' | 'destroy';
  adjustmentId: string | null;
}

interface RecallTaskDto {
  id: string;
  store_id: string;
  recall: string;
  recall_reason: string;
  batch_number: string;
  sku: string;
  expiry_date: string | null;
  qty_locked: number;
  status: RecallTaskUi['status'];
  disposition: RecallTaskUi['disposition'];
  adjustment_id: string | null;
}

const recallTasksKey = (storeId: string | undefined) => [
  'stocks',
  'recall-tasks',
  storeId ?? 'all',
];

/** This store's recall work (every store in scope under "All Stores"). */
export function useRecallTasks(storeId: string | undefined) {
  return useQuery({
    queryKey: recallTasksKey(storeId),
    enabled: useRealApi,
    queryFn: async (): Promise<RecallTaskUi[]> => {
      const rows = await apiFetchList<RecallTaskDto>('/api/inventory/recall-tasks/', {
        query: storeId ? { store_id: storeId } : {},
      });
      return rows.map((t) => ({
        id: t.id,
        storeId: t.store_id,
        recallId: t.recall,
        recallReason: t.recall_reason,
        batch: t.batch_number,
        sku: t.sku,
        expiry: t.expiry_date,
        qty: t.qty_locked,
        status: t.status,
        disposition: t.disposition,
        adjustmentId: t.adjustment_id,
      }));
    },
  });
}

function useRecallAction<T extends { id: string }>(path: (v: T) => string, body: (v: T) => object) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: T) => apiFetch<unknown>(path(v), { method: 'POST', body: body(v) }),
    onSuccess: () =>
      [
        ['stocks', 'recall-tasks'],
        stocksKeys.recalls(),
        stocksKeys.adjustments(),
        ['inventory'],
      ].forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
}

export const useIsolateRecallTask = () =>
  useRecallAction<{ id: string }>(
    ({ id }) => `/api/inventory/recall-tasks/${id}/isolate/`,
    () => ({}),
  );

export const useDisposeRecallTask = () =>
  useRecallAction<{ id: string; disposition: 'supplier_return' | 'destroy'; evidenceRef: string }>(
    ({ id }) => `/api/inventory/recall-tasks/${id}/dispose/`,
    ({ disposition, evidenceRef }) => ({ disposition, evidence_ref: evidenceRef }),
  );

export const useResolveRecall = () =>
  useRecallAction<{ id: string }>(
    ({ id }) => `/api/inventory/recalls/${id}/resolve/`,
    () => ({}),
  );

/** Pharmacist inspection of a quarantined patient return: restock or dispose. */
export function useReturnDisposition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: 'restock' | 'dispose';
      reason: string;
    }) =>
      apiFetch<unknown>(`/api/stocks/returns/${id}/disposition/`, {
        method: 'POST',
        body: { action, reason },
      }),
    onSuccess: () =>
      [stocksKeys.returns(), stocksKeys.adjustments(), ['inventory']].forEach((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
  });
}
